import { Loader2, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import type { TemplateFormField, VideoTemplateDto } from "../../api/videoService";

interface Props {
  template: VideoTemplateDto;
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onGenerate: () => void;
  /** 编排/出图 Job 进行中:输入框 + 按钮禁用,按钮转圈。 */
  busy: boolean;
}

/**
 * 模版「填空式故事配方」面板(2026-05-31)。
 * 模版预设了故事引擎的目的/基调/风格;用户只需填几个空(产品名/卖点…),点一键 → 走服务端
 * 「编排+出图」Job → 出贴牌故事视频。聚焦「填空→一键」,不再让用户面对完整 brief 选择器。
 */
export function VideoTemplateFillPanel({ template, values, onChange, onGenerate, busy }: Props) {
  const fields = (template.formFields ?? []) as unknown as TemplateFormField[];
  const allRequiredFilled = fields
    .filter((f) => f.required)
    .every((f) => (values[f.key] ?? "").trim().length > 0);

  // 占位符替换预览(空值留 {key} 提示用户还没填)
  const preview = template.briefScript.replace(
    /\{(\w+)\}/g,
    (_m, k: string) => (values[k] ?? "").trim() || `{${k}}`
  );

  const presets = [
    template.briefPurpose && { label: "目的", value: template.briefPurpose },
    template.briefTone && { label: "基调", value: template.briefTone },
    template.briefDuration && { label: "时长", value: template.briefDuration },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-primary">填空生成 · {template.name}</div>
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <span
                key={p.label}
                className="rounded-full border border-primary/30 bg-background px-2 py-0.5 text-2xs text-muted-foreground"
              >
                {p.label}·{p.value}
              </span>
            ))}
          </div>
        )}
      </div>

      {template.description && (
        <p className="text-xs text-muted-foreground">{template.description}</p>
      )}

      <div className="space-y-2.5">
        {fields.map((f) => (
          <div key={f.key} className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {f.label}
              {f.required && <span className="ml-0.5 text-destructive">*</span>}
            </Label>
            <Input
              value={values[f.key] ?? ""}
              disabled={busy}
              onChange={(e) => onChange(f.key, e.target.value)}
              placeholder={f.hint || `填写${f.label}`}
              className="text-sm"
            />
          </div>
        ))}
      </div>

      <div className="rounded-md border border-dashed border-primary/20 bg-background/60 p-2">
        <div className="mb-1 text-2xs text-muted-foreground">AI 将据此编一个故事:</div>
        <p className="text-xs leading-relaxed text-foreground/80">{preview}</p>
      </div>

      <Button
        type="button"
        onClick={onGenerate}
        disabled={busy || !allRequiredFilled}
        className="w-full"
      >
        {busy ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-1.5 h-4 w-4" />
        )}
        {busy ? "AI 编排中…" : "一键生成故事视频"}
      </Button>
    </div>
  );
}
